<template>
    <div class="rsvp-detail-page">
        <b-button variant="link" class="back-link mb-3 px-0" @click="$router.back()">
            <i class="fa fa-arrow-left mr-1"></i> Back to Invitations
        </b-button>

        <div v-if="loading" class="text-center py-5">
            <b-spinner variant="primary" label="Loading..." />
            <p class="mt-2 text-muted">Loading invitation details...</p>
        </div>

        <div v-else-if="error">
            <b-alert show variant="danger">
                <i class="fa fa-exclamation-triangle mr-1"></i>
                {{ error }}
            </b-alert>
        </div>

        <div v-else-if="invitation" class="event-detail-layout">
            <!-- Hero Banner -->
            <div class="event-hero" :style="heroStyle">
                <div class="hero-overlay">
                    <div class="hero-content">
                        <span class="status-pill" :class="'status-' + currentStatus">
                            {{ statusLabel }}
                        </span>
                        <h1 class="hero-event-name">{{ ws.name }}</h1>
                        <p v-if="ws.venue" class="hero-venue">
                            <i class="fa fa-map-marker mr-1"></i>
                            {{ ws.venue }}<span v-if="ws.address">, {{ ws.address }}</span>
                        </p>
                    </div>
                </div>
            </div>

            <!-- Event Info Cards -->
            <div class="info-grid">
                <div v-if="ws.event_date" class="info-card">
                    <div class="info-icon" style="background: #ebf5fb;">
                        <i class="fa fa-calendar" style="color: #2980b9;"></i>
                    </div>
                    <div class="info-content">
                        <span class="info-label">Date & Time</span>
                        <span class="info-value">{{ formattedDate }}</span>
                        <span v-if="formattedEndDate" class="info-sub">to {{ formattedEndDate }}</span>
                    </div>
                </div>

                <div v-if="ws.venue" class="info-card">
                    <div class="info-icon" style="background: #fef5e7;">
                        <i class="fa fa-map-marker" style="color: #e67e22;"></i>
                    </div>
                    <div class="info-content">
                        <span class="info-label">Venue</span>
                        <span class="info-value">{{ ws.venue }}</span>
                        <span v-if="ws.address" class="info-sub">{{ ws.address }}</span>
                    </div>
                </div>

                <div v-if="ws.rsvp_deadline" class="info-card">
                    <div class="info-icon" :style="deadlinePassed ? 'background: #fdedec;' : 'background: #fef9e7;'">
                        <i class="fa fa-clock-o" :style="deadlinePassed ? 'color: #e74c3c;' : 'color: #f39c12;'"></i>
                    </div>
                    <div class="info-content">
                        <span class="info-label">RSVP Deadline</span>
                        <span v-if="deadlinePassed" class="info-value text-danger font-weight-bold">Passed ({{ formattedDeadline }})</span>
                        <span v-else class="info-value">{{ formattedDeadline }}</span>
                    </div>
                </div>

                <div v-if="ws.max_capacity" class="info-card">
                    <div class="info-icon" style="background: #eafaf1;">
                        <i class="fa fa-users" style="color: #27ae60;"></i>
                    </div>
                    <div class="info-content">
                        <span class="info-label">Capacity</span>
                        <span class="info-value">{{ ws.max_capacity }} attendees</span>
                    </div>
                </div>
            </div>

            <!-- Description -->
            <div v-if="ws.event_description" class="description-section">
                <h6 class="section-title">
                    <i class="fa fa-info-circle mr-2"></i>About This Event
                </h6>
                <div class="description-body" v-html="ws.event_description"></div>
            </div>

            <!-- Response Section -->
            <div class="response-section">
                <div v-if="responseState === 'idle' && canRespond && deadlinePassed" class="respond-prompt text-center">
                    <div class="mb-3">
                        <i class="fa fa-exclamation-triangle text-warning" style="font-size: 2.5rem;"></i>
                    </div>
                    <h5 class="mb-2">RSVP Deadline Has Passed</h5>
                    <p class="text-muted">The RSVP deadline for this event was {{ formattedDeadline }}. You can no longer submit a response.</p>
                </div>

                <div v-else-if="responseState === 'idle' && canRespond" class="respond-prompt">
                    <h5 class="mb-1">Will you be attending?</h5>
                    <p class="text-muted small mb-3">Let the organizer know your plans.</p>
                    <div class="respond-buttons">
                        <b-button
                            variant="success"
                            size="lg"
                            class="respond-btn accept-btn"
                            :disabled="submitting"
                            @click="respond('accepted')"
                        >
                            <b-spinner v-if="submitting && pendingResponse === 'accepted'" small class="mr-1" />
                            <i v-else class="fa fa-check mr-1"></i>
                            Accept Invitation
                        </b-button>
                        <b-button
                            variant="outline-danger"
                            size="lg"
                            class="respond-btn"
                            :disabled="submitting"
                            @click="respond('declined')"
                        >
                            <b-spinner v-if="submitting && pendingResponse === 'declined'" small class="mr-1" />
                            <i v-else class="fa fa-times mr-1"></i>
                            Decline
                        </b-button>
                    </div>
                </div>

                <div v-else-if="responseState === 'accepted'" class="confirmed-section">
                    <div class="confirmed-icon">
                        <i class="fa fa-check"></i>
                    </div>
                    <h4 class="text-success mb-2">You're Confirmed!</h4>
                    <p class="text-muted">{{ responseResult.message || 'We look forward to seeing you at the event.' }}</p>
                </div>

                <div v-else-if="responseState === 'declined'" class="declined-section">
                    <div class="declined-icon">
                        <i class="fa fa-info"></i>
                    </div>
                    <h5 class="mb-2">Invitation Declined</h5>
                    <p class="text-muted">Thank you for letting us know. If you change your mind, contact the organizer.</p>
                </div>

                <div v-else-if="!canRespond" class="already-responded">
                    <div class="responded-icon" :class="currentStatus === 'accepted' ? 'icon-success' : 'icon-muted'">
                        <i :class="currentStatus === 'accepted' ? 'fa fa-check-circle' : 'fa fa-info-circle'"></i>
                    </div>
                    <h5 v-if="currentStatus === 'accepted'" class="text-success mb-1">You're Attending!</h5>
                    <h5 v-else class="mb-1">Response Recorded</h5>
                    <p class="text-muted small mb-0">
                        Your RSVP status: <strong :class="currentStatus === 'accepted' ? 'text-success' : 'text-danger'">{{ statusLabel }}</strong>
                    </p>
                </div>
            </div>

            <!-- Surveys Section -->
            <div v-if="surveys.length > 0" class="surveys-section">
                <h6 class="section-title">
                    <i class="fa fa-clipboard mr-2"></i>Surveys
                </h6>
                <div v-for="survey in surveys" :key="survey.id" class="survey-item" @click="openSurvey(survey)">
                    <div class="survey-item-content">
                        <div class="survey-item-title">{{ survey.title }}</div>
                        <div v-if="survey.description" class="survey-item-desc">{{ survey.description }}</div>
                    </div>
                    <div class="survey-item-status">
                        <span v-if="survey.has_completed" class="badge-completed">
                            <i class="fa fa-check-circle mr-1"></i> Completed
                        </span>
                        <span v-else class="badge-pending">
                            <i class="fa fa-pencil mr-1"></i> Fill Out
                        </span>
                    </div>
                </div>
            </div>

            <!-- Schedule Section -->
            <div v-if="scheduleItems.length > 0" class="schedule-section">
                <h6 class="section-title">
                    <i class="fa fa-calendar mr-2"></i>Event Schedule
                </h6>
                <div class="schedule-timeline">
                    <div v-for="item in scheduleItems" :key="item.id" class="schedule-entry" :class="{ 'is-break': item.is_break }">
                        <div class="schedule-time">
                            <span class="time-start">{{ formatTime(item.start_time) }}</span>
                            <span v-if="item.end_time" class="time-end">{{ formatTime(item.end_time) }}</span>
                        </div>
                        <div class="schedule-dot"></div>
                        <div class="schedule-content">
                            <div class="schedule-title">{{ item.title }}</div>
                            <div v-if="item.speaker_name" class="schedule-speaker">
                                <i class="fa fa-user mr-1"></i> {{ item.speaker_name }}
                                <span v-if="item.speaker_title">, {{ item.speaker_title }}</span>
                            </div>
                            <div v-if="item.location" class="schedule-location">
                                <i class="fa fa-map-marker mr-1"></i> {{ item.location }}
                            </div>
                            <div v-if="item.description" class="schedule-desc">{{ item.description }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'RSVPResponse',
    data() {
        return {
            loading: true,
            error: null,
            invitation: null,
            submitting: false,
            pendingResponse: null,
            responseResult: {},
            responseState: 'idle',
            surveys: [],
            scheduleItems: [],
        }
    },
    computed: {
        invitationId() {
            return this.$route.params.invitationId
        },
        ws() {
            return this.invitation?.workshop || this.invitation?.event || {}
        },
        heroStyle() {
            if (this.ws.flyer_url) {
                return { backgroundImage: `url(${this.ws.flyer_url})` }
            }
            return {}
        },
        currentStatus() {
            if (this.responseState === 'accepted') return 'accepted'
            if (this.responseState === 'declined') return 'declined'
            return this.invitation?.rsvp_status || 'pending'
        },
        statusLabel() {
            const map = { accepted: 'Accepted', declined: 'Declined', sent: 'Awaiting Response', pending: 'Pending' }
            return map[this.currentStatus] || this.currentStatus
        },
        canRespond() {
            return ['pending', 'sent'].includes(this.invitation?.rsvp_status)
        },
        formattedDate() {
            return this.formatDateTime(this.ws.event_date)
        },
        formattedEndDate() {
            return this.formatDateTime(this.ws.event_end_date)
        },
        formattedDeadline() {
            if (!this.ws.rsvp_deadline) return ''
            const d = new Date(this.ws.rsvp_deadline)
            return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
        },
        deadlinePassed() {
            if (!this.ws?.rsvp_deadline) return false
            return new Date(this.ws.rsvp_deadline) < new Date()
        },
    },
    async mounted() {
        await this.loadInvitation()
    },
    methods: {
        formatDateTime(dateStr) {
            if (!dateStr) return ''
            const d = new Date(dateStr)
            return d.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })
        },
        async loadInvitation() {
            this.loading = true
            this.error = null
            try {
                const rsvps = await this.$store.dispatch('eventInvitation/fetchMyRSVPs')
                this.invitation = rsvps.find(
                    (r) => String(r.id) === String(this.invitationId)
                )
                if (!this.invitation) {
                    this.error = 'Invitation not found.'
                } else {
                    this.loadSurveys()
                    this.loadSchedule()
                }
            } catch (err) {
                this.error = err?.response?.data?.error?.message || 'Failed to load invitation details.'
            } finally {
                this.loading = false
            }
        },
        async loadSurveys() {
            try {
                this.surveys = await this.$store.dispatch('eventInvitation/fetchMySurveys', this.invitationId)
            } catch (e) {}
        },
        async loadSchedule() {
            try {
                const data = await this.$store.dispatch('eventInvitation/fetchMySchedule', this.invitationId)
                this.scheduleItems = data?.schedule || []
            } catch (e) {}
        },
        openSurvey(survey) {
            this.$router.push(`/customer-portal/rsvp/${this.invitationId}/survey/${survey.id}`)
        },
        formatTime(dateStr) {
            if (!dateStr) return ''
            const d = new Date(dateStr)
            return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
        },
        async respond(response) {
            this.submitting = true
            this.pendingResponse = response
            try {
                const result = await this.$store.dispatch('eventInvitation/respondToRSVP', {
                    invitationId: this.invitationId,
                    response,
                })
                this.responseResult = result
                this.responseState = response
                this.invitation.rsvp_status = result.rsvp_status || response
            } catch (err) {
                this.$bvToast.toast(
                    err?.response?.data?.error?.message || 'Failed to submit your response. Please try again.',
                    { title: 'Error', variant: 'danger', solid: true }
                )
            } finally {
                this.submitting = false
                this.pendingResponse = null
            }
        },
    },
}
</script>

<style scoped>
.back-link {
    font-weight: 600;
    font-size: 0.9rem;
    text-decoration: none;
}

/* Hero */
.event-hero {
    position: relative;
    height: 220px;
    border-radius: 12px;
    overflow: hidden;
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
    background-size: cover;
    background-position: center;
    margin-bottom: 24px;
}
.hero-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.15) 100%);
    display: flex;
    align-items: flex-end;
}
.hero-content {
    padding: 28px;
    width: 100%;
}
.hero-event-name {
    color: #fff;
    font-size: 1.75rem;
    font-weight: 800;
    margin: 0 0 6px;
    line-height: 1.2;
    text-shadow: 0 2px 6px rgba(0,0,0,0.3);
}
.hero-venue {
    color: rgba(255,255,255,0.8);
    font-size: 0.95rem;
    margin: 0;
}
.status-pill {
    display: inline-block;
    padding: 4px 14px;
    border-radius: 20px;
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 10px;
}
.status-accepted { background: rgba(39, 174, 96, 0.9); color: #fff; }
.status-declined { background: rgba(231, 76, 60, 0.9); color: #fff; }
.status-sent, .status-pending { background: rgba(241, 196, 15, 0.9); color: #333; }

/* Info Grid */
.info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 16px;
    margin-bottom: 24px;
}
.info-card {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    background: #fff;
    border-radius: 10px;
    padding: 16px;
    box-shadow: 0 1px 6px rgba(0,0,0,0.06);
    border: 1px solid #f0f0f0;
}
.info-icon {
    width: 42px;
    height: 42px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}
.info-icon i {
    font-size: 1.1rem;
}
.info-content {
    display: flex;
    flex-direction: column;
}
.info-label {
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    color: #999;
    margin-bottom: 2px;
}
.info-value {
    font-size: 0.92rem;
    font-weight: 600;
    color: #333;
}
.info-sub {
    font-size: 0.82rem;
    color: #888;
}

/* Description */
.description-section {
    background: #fff;
    border-radius: 10px;
    padding: 20px 24px;
    box-shadow: 0 1px 6px rgba(0,0,0,0.06);
    border: 1px solid #f0f0f0;
    margin-bottom: 24px;
}
.section-title {
    font-weight: 700;
    color: #2c3e50;
    margin-bottom: 12px;
    font-size: 0.95rem;
}
.description-body {
    font-size: 0.9rem;
    line-height: 1.7;
    color: #555;
}

/* Response Section */
.response-section {
    background: #fff;
    border-radius: 10px;
    padding: 28px;
    box-shadow: 0 1px 6px rgba(0,0,0,0.06);
    border: 1px solid #f0f0f0;
    text-align: center;
}
.respond-prompt h5 {
    font-weight: 700;
    color: #2c3e50;
}
.respond-buttons {
    display: flex;
    justify-content: center;
    gap: 12px;
    flex-wrap: wrap;
}
.respond-btn {
    border-radius: 8px;
    font-weight: 600;
    padding: 10px 32px;
}
.accept-btn {
    background: linear-gradient(135deg, #27ae60, #2ecc71);
    border: none;
}
.accept-btn:hover {
    background: linear-gradient(135deg, #219a52, #27ae60);
}

.confirmed-section, .declined-section, .already-responded {
    padding: 16px 0;
}
.confirmed-icon, .declined-icon, .responded-icon {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
}
.confirmed-icon {
    background: linear-gradient(135deg, #27ae60, #2ecc71);
    color: #fff;
    font-size: 1.5rem;
}
.declined-icon {
    background: #f0f2f5;
    color: #999;
    font-size: 1.5rem;
}
.responded-icon {
    font-size: 2.5rem;
}
.icon-success i { color: #27ae60; }
.icon-muted i { color: #adb5bd; }

/* Surveys */
.surveys-section, .schedule-section {
    background: #fff;
    border-radius: 10px;
    padding: 20px 24px;
    box-shadow: 0 1px 6px rgba(0,0,0,0.06);
    border: 1px solid #f0f0f0;
    margin-top: 24px;
}
.survey-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    border-radius: 8px;
    border: 1px solid #f0f0f0;
    margin-bottom: 10px;
    cursor: pointer;
    transition: background 0.15s;
}
.survey-item:hover {
    background: #f8f9fa;
}
.survey-item:last-child {
    margin-bottom: 0;
}
.survey-item-title {
    font-weight: 600;
    font-size: 0.92rem;
    color: #333;
}
.survey-item-desc {
    font-size: 0.82rem;
    color: #888;
    margin-top: 2px;
}
.badge-completed {
    color: #27ae60;
    font-size: 0.82rem;
    font-weight: 600;
}
.badge-pending {
    color: #3498db;
    font-size: 0.82rem;
    font-weight: 600;
}

/* Schedule Timeline */
.schedule-timeline {
    padding-left: 8px;
}
.schedule-entry {
    display: flex;
    gap: 16px;
    padding: 12px 0;
    border-bottom: 1px solid #f5f5f5;
}
.schedule-entry:last-child {
    border-bottom: none;
}
.schedule-entry.is-break {
    opacity: 0.7;
}
.schedule-time {
    min-width: 80px;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    flex-shrink: 0;
}
.time-start {
    font-weight: 600;
    font-size: 0.85rem;
    color: #333;
}
.time-end {
    font-size: 0.78rem;
    color: #999;
}
.schedule-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #3498db;
    margin-top: 5px;
    flex-shrink: 0;
}
.is-break .schedule-dot {
    background: #f39c12;
}
.schedule-content {
    flex: 1;
}
.schedule-title {
    font-weight: 600;
    font-size: 0.92rem;
    color: #333;
}
.schedule-speaker, .schedule-location {
    font-size: 0.82rem;
    color: #888;
    margin-top: 2px;
}
.schedule-desc {
    font-size: 0.82rem;
    color: #666;
    margin-top: 4px;
    line-height: 1.5;
}

@media (max-width: 576px) {
    .event-hero { height: 180px; }
    .hero-event-name { font-size: 1.35rem; }
    .hero-content { padding: 20px; }
    .info-grid { grid-template-columns: 1fr; }
    .respond-btn { width: 100%; }
    .schedule-time { min-width: 60px; }
}
</style>
